import React from 'react';
import { TypographyProps as MuiTypographyProps } from '@mui/material';
import { StyledTypography } from './Typography.style';

type MuiVariant = MuiTypographyProps['variant'];
type CustomVariant = 'display1' | 'display2' | 'display3' | 'display4';

export interface TypographyProps extends Omit<MuiTypographyProps, 'variant'> {
  /**
   * The variant of the typography
   */
  variant?: MuiVariant | CustomVariant;
  /**
   * The color of the text
   */
  color?: 'primary' | 'secondary' | 'textPrimary' | 'textSecondary' | 'error' | 'warning' | 'info' | 'success' | 'inherit' | 'white' | 'black';
  /**
   * Whether the text should be truncated with ellipsis
   */
  truncate?: boolean;
  /**
   * Number of lines to show before truncating (for multi-line truncation)
   */
  lines?: number;
  /**
   * Whether to apply gradient text effect
   */
  gradient?: boolean;
  /**
   * Custom gradient colors (only applies when gradient is true)
   */
  gradientColors?: [string, string];
  /**
   * Whether to apply text shadow effect
   */
  textShadow?: boolean;
  /**
   * Custom text shadow (only applies when textShadow is true)
   */
  shadowColor?: string;
}

export const Typography: React.FC<TypographyProps> = ({
  children,
  variant = 'body1',
  color = 'textPrimary',
  truncate = false,
  lines,
  gradient = false,
  gradientColors,
  textShadow = false,
  shadowColor,
  sx,
  ...props
}) => {
  // Handle custom display variants by mapping them to h1 for MUI
  const muiVariant: MuiVariant = variant?.startsWith('display') ? 'h1' : variant as MuiVariant;
  
  return (
    <StyledTypography
      variant={muiVariant}
      color={color}
      truncate={truncate}
      lines={lines}
      gradient={gradient}
      gradientColors={gradientColors}
      textShadow={textShadow}
      shadowColor={shadowColor}
      sx={sx}
      {...props}
    >
      {children}
    </StyledTypography>
  );
}; 